/**
 * useInventory — React hook for the local player's inventory state.
 *
 * Event-driven: `InventorySystem` is EventBus-native (`on(event, cb)` →
 * unsubscribe fn) and emits `goldChanged` / `itemEquipped` /
 * `itemUnequipped` / `itemConsumed`. We seed from the system's getters on
 * mount, then re-snapshot the full item/gold view on every relevant event.
 * No polling — components never need ServiceRegistry/setInterval directly.
 *
 * `removeItem` (drop) and `sort` mutate storage WITHOUT emitting, so those
 * actions re-snapshot synchronously after the call. `use` (consume) emits
 * `itemConsumed`, which the subscription already catches — but it also
 * re-snapshots to stay consistent and instant.
 *
 * Note: `itemAdded` / `itemRemoved` are declared in `INVENTORY_EVENTS` but
 * never emitted by the system today; add/remove happen as side effects of
 * equip/consume, which DO emit. If the system starts emitting add/remove
 * later, subscribing here is a one-line addition.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import type { InventorySystem } from '../../systems/inventory/InventorySystem';
import type { InventoryItem } from '../../../shared/CharacterTypes';

/** Event names the InventorySystem actually emits (via `on('<event>', cb)`). */
const INVENTORY_EVENTS = ['snapshotUpdated', 'goldChanged', 'itemEquipped', 'itemUnequipped', 'itemConsumed'] as const;

export type InventorySortBy = 'type' | 'name' | 'rarity';

/** Default bag capacity when unstated by storage (GameState.ts:181, InventorySystem.ts:40). */
export const DEFAULT_INVENTORY_MAX_SLOTS = 50;

export interface InventoryState {
  items: InventoryItem[];
  gold: number;
  emptySlots: number;
  maxSlots: number;
}

export interface InventoryActions {
  /** Consume/use an item by id (delegates to InventorySystem.consume). */
  use: (itemId: string) => void;
  /** Drop (remove) `qty` of an item by id; defaults to 1. */
  drop: (itemId: string, qty?: number) => void;
  /** Re-sort the inventory in place; defaults to sorting by type. */
  sort: (by?: InventorySortBy) => void;
}

/** Resolve the InventorySystem singleton without throwing if unregistered. */
function getInventorySystem(): InventorySystem | undefined {
  try {
    return ServiceRegistry.getInstance().get('InventorySystem', { optional: true });
  } catch {
    return undefined;
  }
}

export function useInventory(): InventoryState & InventoryActions {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [gold, setGold] = useState(0);
  const [emptySlots, setEmptySlots] = useState(0);
  const [maxSlots, setMaxSlots] = useState<number>(DEFAULT_INVENTORY_MAX_SLOTS);

  // Hold the snapshot fn in a ref so the action callbacks (created once via
  // useCallback) can trigger a fresh read after mutations the system doesn't
  // emit for (drop/sort), without re-subscribing.
  const snapshotRef = useRef<() => void>(() => {});

  useEffect(() => {
    // Same persisted-open boot race as useEquipment (CHAR-MUT-RECONNECT-01):
    // a bag window restored open at page boot mounts before GameScene
    // registers InventorySystem, so a one-shot resolve would never subscribe.
    // Arm now when possible; re-arm on GAME_SCENE_READY (emitted strictly
    // after registration; also rebinds across same-page scene reboots).
    let unsubs: Array<() => void> = [];
    const arm = () => {
      for (const unsub of unsubs) unsub();
      unsubs = [];
      const sys = getInventorySystem();
      if (!sys) return;

      const snapshot = () => {
        // `save()` returns a fresh copy of the backing inventory (items +
        // gold); emptySlots comes off the system getter. Copy items again so
        // React sees a new array reference and re-renders.
        const save = sys.save();
        setItems([...save.inventory.items]);
        setGold(save.inventory.gold);
        setEmptySlots(sys.getEmptySlots());
        if (typeof (save.inventory as any).maxSlots === 'number') {
          setMaxSlots((save.inventory as any).maxSlots);
        }
      };
      snapshotRef.current = snapshot;

      snapshot();

      unsubs = INVENTORY_EVENTS.map((event) => sys.on(event, snapshot));
    };
    arm();
    const unsubReady = EventBus.getInstance().on(EVENTS.GAME_SCENE_READY, arm);
    return () => {
      unsubReady();
      for (const unsub of unsubs) unsub();
    };
  }, []);

  const use = useCallback((itemId: string) => {
    getInventorySystem()?.consume(itemId);
    snapshotRef.current();
  }, []);

  const drop = useCallback((itemId: string, qty: number = 1) => {
    getInventorySystem()?.removeItem(itemId, qty);
    snapshotRef.current();
  }, []);

  const sort = useCallback((by: InventorySortBy = 'type') => {
    getInventorySystem()?.sort(by);
    snapshotRef.current();
  }, []);

  return { items, gold, emptySlots, maxSlots, use, drop, sort };
}
